<template>
  <div
    class="upgrade-card"
    :class="{ bought: isPurchased, unavailable: !canBePurchased }"
    @click="handleClick"
  >
    <div class="upgrade-title">{{ upgrade.name }}</div>
    <div class="upgrade-cost">Cost: ${{ formatNumber(upgrade.cost) }}</div>
    <div class="upgrade-description">{{ upgrade.description }}</div>

    <div v-if="isPurchased" class="upgrade-purchased">✓ Purchased</div>

    <div v-if="!isPurchased && !canBePurchased" class="upgrade-requirements">
      <div v-if="missingRequirements.length > 0" class="requirements-missing">
        <div class="requirements-title">Missing:</div>
        <div v-for="(req, index) in missingRequirements" :key="index" class="requirement-item">
          • {{ req }}
        </div>
      </div>
      <div v-else-if="purchaseCheck.reason" class="requirement-item">
        • {{ purchaseCheck.reason }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useGameStore } from '@/stores/gameStore';
import { formatNumber } from '@/utils/gameUtils';
import type { UpgradeEntity } from '@/types/UpgradeEntity';
import { checkUpgradePurchase } from '@/types/UpgradeEntity';

interface Props {
  upgrade: UpgradeEntity;
}

const props = defineProps<Props>();
const store = useGameStore();

const purchaseCheck = computed(() => checkUpgradePurchase(props.upgrade, store.state));
const isPurchased = computed(() => store.hasUpgrade(props.upgrade.id));
const canBePurchased = computed(() => purchaseCheck.value.allowed);
const missingRequirements = computed(() => props.upgrade.getMissingRequirements(store.state));

const handleClick = () => {
  if (!isPurchased.value && canBePurchased.value) {
    store.buyUpgrade(props.upgrade.id);
  }
};
</script>

<style scoped>
.upgrade-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #444;
  border-radius: 8px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 10px;
}

.upgrade-card:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #00ff88;
  transform: translateY(-2px);
}

.upgrade-card.bought {
  background: rgba(0, 255, 136, 0.1);
  border-color: #00ff88;
  cursor: default;
}

.upgrade-card.bought:hover {
  transform: none;
}

.upgrade-card.unavailable {
  opacity: 0.6;
  cursor: not-allowed;
}

.upgrade-card.unavailable:hover {
  transform: none;
  border-color: #444;
}

.upgrade-title {
  font-size: 1.2em;
  font-weight: bold;
  color: #00ff88;
  margin-bottom: 8px;
}

.upgrade-cost {
  color: #ffaa00;
  font-weight: bold;
  margin-bottom: 8px;
}

.upgrade-description {
  color: #ccc;
  font-size: 0.9em;
  line-height: 1.4;
  margin-bottom: 8px;
}

.upgrade-purchased {
  color: #00ff88;
  font-weight: bold;
  text-align: center;
  margin-top: 8px;
}

.upgrade-requirements {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #444;
}

.requirements-missing {
  color: #ff6b6b;
  font-size: 0.85em;
}

.requirements-title {
  font-weight: bold;
  margin-bottom: 4px;
  color: #ff8888;
}

.requirement-item {
  color: #ffaaaa;
  font-size: 0.85em;
  margin-top: 2px;
  line-height: 1.4;
}
</style> 